import RadioButtons from "./RadioButtons/RadioButtons";
import TextArea from "./TextArea/TextArea";
import TextInput from "./TextInput/TextInput";
import NumberInput from "./NumberInput/NumberInput";
import ImperialNumberField from "./ImperialInput/ImperialNumberField";
import Select from "../../../components/Select/Select";
import CheckBoxList from "./CheckBoxList/CheckBoxList";
import PhotoDiagnose from "./PhotoDiagnose/PhotoDiagnose";
import DocumentUpload from "./DocumentUpload/DocumentUpload";
import { useEffect } from "react";
import { useFunnel, useShellId } from "../VariantB/shellContext";
import * as translator from "../../../utils/translator";
import DatePickerWrapper from "../../../components/DatePickerWrapper/DatePickerWrapper";

// Questionnaire date questions ask about things that already happened ("since when have you been
// taking this medication?"), so a future date is never a valid answer. Evaluated per render so the
// bound stays correct across midnight / long-lived sessions.
const questionnaireMaxDate = () => new Date();


export const validateQuestion = (question) => {
  let isValid = true;

  if (question.answerGroup.recipe.type.includes("check_list")) {
    return question.answer.includes("[+]");
  }

  if (!question.answer && question.answer !== 0) {
    isValid = false;
  }

  return isValid;
};

export const formatQuestionnaire = (questionnaire, answersSelectedMap) => {
  questionnaire.forEach((question) => {
    if (question.answerGroup?.recipe?.type === "check_list") {
      let selectedOptions = [];

      question.answerGroup.children.forEach((optionNode) => {
        let formattedOptionValue =
          (answersSelectedMap[optionNode.recipe.id] ? "[+] " : "[-] ") +
          optionNode.recipe.value;
        selectedOptions.push(formattedOptionValue);
      });

      if (selectedOptions.length > 0) {
        question.answer = selectedOptions.join("\n");
      }
    } else {
      if (Array.isArray(question.answer)) {
        question.answer = question.answer[0];
      }
    }
  });


  return questionnaire;
};

export const bundleQuestionnaireForSending = (
  flattenedQuestionnaire,
  answersSelectedMap,
  revealedAnswerGroups
) => {
  let flattenedFiltred = JSON.parse(JSON.stringify(flattenedQuestionnaire));
  let bundledQuestionnaire = [];


  //for immediate answer groups, only keep selected answers
  flattenedFiltred.forEach((question) => {

    // Mirrors QuestionnaireVariantB's computeRevealedForSubmit: a question gated
    // behind another answer (conditional_answer_id) is excluded entirely once
    // that gate is no longer satisfied, so a stale answer from before the user
    // changed their mind can't leak into the submission.
    if (question.conditional_answer_id && !answersSelectedMap[question.conditional_answer_id]) {
      return;
    }

    // Special handling for bmi_question type
    if (question.question_type === "bmi_question") {
      const heightAnswerId = question.answersGroup?.recipe?.id;
      const weightAnswerNode = question.answersGroup?.children?.[0];
      const weightAnswerId = weightAnswerNode?.recipe?.id;

      let questionObject = {
        question_id: question.id,
        question: question.text,
        question_type: question.question_type,
        answerGroup: question.answersGroup,
        answer: answersSelectedMap[heightAnswerId],
        weight_answer: answersSelectedMap[weightAnswerId],
        answer_id: [heightAnswerId, weightAnswerId].filter(Boolean),
        specification: null,
        auto_processing: null,
        hide_from_doctor: question.hide_from_doctor,
        show_to_pharmacy: question.show_to_pharmacy,
        is_answer_blocking: null,
        is_prescription_diagnosis: question.is_prescription_diagnosis,
        specification_data: [],
      };

      bundledQuestionnaire.push(questionObject);
      return; // skip additional subquestion processing for bmi_question
    }

    //save answers of questions WITH children
    if (question.answersGroup?.children) {
      let filteredChildren = question.answersGroup?.children?.filter(
        (child) => answersSelectedMap[child.recipe.id] || answersSelectedMap[child.recipe.id] === 0
      );

      let answer;
      let answerID;

      if (
        ["number", "textarea", "text","date"].includes(
          question.answersGroup.recipe.type
        )
      ) {
       
        answer = answersSelectedMap[question.answersGroup.recipe.id];
        answerID = [question.answersGroup.recipe.id];
      } else {
        answer = filteredChildren.map((child, index) => child.recipe.value);
        answerID = filteredChildren.map((child) => child.recipe.id);
      }

      let questionObject = {
        question_id: question.id,
        question: question.text,
        question_type: question.question_type,
        answerGroup: question.answersGroup,
        answer,
        answer_id: answerID,
        specification: null,
        auto_processing: null,
        hide_from_doctor: question.hide_from_doctor,
        show_to_pharmacy: question.show_to_pharmacy,
        is_answer_blocking: null,
        is_prescription_diagnosis: question.is_prescription_diagnosis,
        specification_data: [],
      };

      bundledQuestionnaire.push(questionObject);
    } else if (question.question_type === "title") {
      let questionObject = {
        question_id: question.id,
        question: question.text,
        question_type: question.question_type,
        answerGroup: question.answersGroup,
        answer: null,
        answer_id: [],
        specification: null,
        auto_processing: null,
        hide_from_doctor: question.hide_from_doctor,
        show_to_pharmacy: question.show_to_pharmacy,
        is_answer_blocking: null,
        specification_data: [],
      };

      bundledQuestionnaire.push(questionObject);
    }

    //for additional answerGroups (aka subquestions), only keep selected answers

    if (question.additional) {
      question.additional.forEach((subquestion) => {
        //ONLY IF THE SUBQUESTION IS EVEN REVEALED
        if (revealedAnswerGroups[subquestion.recipe.id]) {
          subquestion.children = subquestion.children?.filter(
            (child) => answersSelectedMap[child.recipe.id]
          );

          let answer;
          //save answers of questions WITHOUT children
          if (["textarea","date", "number", "text"].includes(subquestion.recipe.type)) {
            answer = answersSelectedMap[subquestion.recipe.id];
          } else {
            answer = subquestion.children.map((child) => child.recipe.value);
          }

          let questionObject = {
            question_id: subquestion.recipe.id,
            answerGroup: subquestion,
            question:
              subquestion.recipe.long_label ||
              subquestion.recipe.label ||
              subquestion.recipe.text ||
              subquestion.recipe.value,
            question_type: "subquestion_" + subquestion.recipe.type,
            answer,
            answer_id: subquestion.children?.map((child) => child.recipe.id),
            specification: null,
            auto_processing: null,
            hide_from_doctor: subquestion.hide_from_doctor,
            show_to_pharmacy: subquestion.show_to_pharmacy,
            is_answer_blocking: null,
            specification_data: [],
          };

          bundledQuestionnaire.push(questionObject);
        }
      });
    }
  });

  return bundledQuestionnaire;
};

export const CHECKBOX_OPTION = "checkbox_option";
export const CHECKBOX = 'checkbox';

// WL disease multi/select questions where "none of the above" must render last.
// IDs from weight_loss questionnaire recipe tree (stable across locales).
const PIN_EXCLUSIVE_NONE_LAST_QUESTION_IDS = new Set([1009, 1011]);
// Legacy pin-last fallback ids when validation_rule.is_exclusive_none is unset.
// Prefer DB flag is_exclusive_none; do not grow this set.
export const EXCLUSIVE_NONE_ANSWER_IDS = new Set([3897]);
const PIN_EXCLUSIVE_NONE_LAST_ANSWER_IDS = EXCLUSIVE_NONE_ANSWER_IDS;

export const orderAnswerOptionsChildren = (children, recipeQuestionId) => {
  if (!children?.length) return children || [];
  if (!PIN_EXCLUSIVE_NONE_LAST_QUESTION_IDS.has(Number(recipeQuestionId))) {
    return children;
  }
  const normal = [];
  const exclusiveNone = [];
  for (const child of children) {
    const recipe = child?.recipe;
    const isExclusiveNone =
      !!recipe?.validation_rule?.is_exclusive_none ||
      PIN_EXCLUSIVE_NONE_LAST_ANSWER_IDS.has(Number(recipe?.id));
    if (isExclusiveNone) exclusiveNone.push(child);
    else normal.push(child);
  }
  return exclusiveNone.length ? [...normal, ...exclusiveNone] : children;
};

// helper components

//dummy componenet to render the labels and texts of subquestions
const SubquestionText = ({ recipeAnswer, answerType = "" }) => {
  const helpBlock = recipeAnswer?.help_block;

  switch (answerType) {
    case "check_list":
      return (
        <>
          <div className="questionnaire-question-item-text-container  ">
            {recipeAnswer.label || recipeAnswer.long_label}
          </div>
          {helpBlock && (
            <div
              className="questionnaire-question-item-helpblock-container"
              dangerouslySetInnerHTML={{ __html: helpBlock }}
            />
          )}
        </>
      );

    default:
      return (
        <>
          <div
            dangerouslySetInnerHTML={{
              __html: recipeAnswer.label || recipeAnswer.long_label,
            }}
            className="questionnaire-question-item-text-container questionnaire-subquestion-text "
          />
          {helpBlock && (
            <div
              className="questionnaire-question-item-helpblock-container"
              dangerouslySetInnerHTML={{ __html: helpBlock }}
            />
          )}
        </>
      );
  }
};

// ---------------------------------------

//this component renders the content of an answer group,
//e.g "select" is a group of "select_options"
export const RenderAnswerGroup = ({
  answersGroup,
  onTextChange,
  onAnswerClick,
  onHide,
  onReveal,
  questionnaireValidationObject,
  answersSelectedMap,
  disabled = false,
  isImperial = false,
  files = [],
  setFiles = () => {},
  language,
  category,
  showUploadError = false,
  // VariantB: weekday headers follow funnel language. A top-level date answer is
  // the date of birth, and the minimum age is 18, so the calendar is capped at
  // (and opens on) today − 18y like every other birthday picker. The underage
  // blocker modal on Continue stays as the safety net for typed dates.
  // Medication "since when" dates use RenderAnswerGroupAsSubquestion (always
  // non-birthday, capped at today).
  isVariantB = false,
  // Variant B only: set false for a top-level date answer that is not a DOB.
  isBirthdayQuestion = true,
}) => {
  // VariantB renders both shells at once; outside it there is no provider and
  // the id is returned unchanged, so the classic questionnaire keeps bare ids.
  const answerDomId = useShellId(answersGroup?.recipe?.id ? "answer_" + answersGroup.recipe.id : undefined);
  // HL-12357: the WL3 date of birth shows its dd/mm/yyyy mask as the placeholder.
  const isWl3 = useFunnel() === "wl3";

  useEffect(() => {
    if (!answersGroup) return;
    onReveal(answersGroup);

    return () => {
      onHide(answersGroup);
    };
  }, []);

  if (!answersGroup) return null;

  switch (answersGroup.recipe?.type) {
    case "radio_buttons":
      return (
        <RadioButtons
          errorMode={
            questionnaireValidationObject[answersGroup.recipe.id] === false
          }
          onAnswerClick={onAnswerClick}
          children={orderAnswerOptionsChildren(
            answersGroup.children,
            answersGroup.recipe.recipe_question_id
          )}
          answersSelectedMap={answersSelectedMap}
        />
      );

    case "photo_diagnose":
      return (
        <PhotoDiagnose
          language={language}
          category={category}
          files={files}
          setFilesCallback={setFiles}
          showError={showUploadError}
        />
      );

    case "document_upload":
      return (
        <DocumentUpload
          language={language}
          files={files}
          setFilesCallback={setFiles}
          showError={showUploadError}
        />
      );

    case "select": {
      const orderedSelectChildren = orderAnswerOptionsChildren(
        answersGroup.children,
        answersGroup.recipe.recipe_question_id
      );
      let filteredChildren = orderedSelectChildren.filter(option => {
        return answersSelectedMap[option.recipe.id];
      });

      //if there is an initialValue that came from prefill (customerAnswers) we pass it
      let initialValue = filteredChildren.length ? filteredChildren[0].recipe : null

      return (
        <Select
          disabled={disabled}
          errorMode={
            questionnaireValidationObject[answersGroup.recipe.id] === false
          }
          onAnswerClick={onAnswerClick}
          page={"questionnaire"}
          options={orderedSelectChildren}
          id={answerDomId}
          initialValue={initialValue}
          style={{
            border: "1px solid #aaa",
            height: "44px",
            width: "250px",
            borderRadius: "3px",
            backgroundColor: "transparent",
            margin: "0px"
            }}
        />
      );
    }

    case "check_list":
      return (
        <CheckBoxList
          errorMode={
            questionnaireValidationObject[answersGroup.recipe.id] === false
          }
          onAnswerClick={onAnswerClick}
          children={orderAnswerOptionsChildren(
            answersGroup.children,
            answersGroup.recipe.recipe_question_id
          )}
          answersSelectedMap={answersSelectedMap}
        />
      );

    case "number": {
      const rule = answersGroup.recipe.validation_rule;
      const imperialMode = rule?.allow_imperial_height ? "height" : rule?.allow_imperial_weight ? "weight" : null;
      if (imperialMode) {
        return (
          <ImperialNumberField
            mode={imperialMode}
            answerId={answersGroup.recipe.id}
            validationRule={rule}
            questionnaireValidationObject={questionnaireValidationObject}
            onTextChange={onTextChange}
            recipeQuestionId={answersGroup.recipe.recipe_question_id}
            placeholder={answersGroup.recipe.placeholder}
            value={answersSelectedMap[answersGroup.recipe.id] || ""}
            disabled={disabled}
            isImperial={isImperial}
          />
        );
      }

      return (
        <NumberInput
          disabled={disabled}
          maxValue={rule.max_value}
          minValue={rule.min_value}
          errorMode={
            questionnaireValidationObject[answersGroup.recipe.id] === false
          }
          placeholder={answersGroup.recipe.placeholder}
          onChange={(text) => {
            onTextChange(
              answersGroup.recipe.id,
              text,
              rule,
              answersGroup.recipe.recipe_question_id
            );
          }}
          id={answerDomId}
          value={answersSelectedMap[answersGroup.recipe.id]}
        />
      );
    }

    case "text":
      return (
        <TextInput
          disabled={disabled}
          errorMode={
            questionnaireValidationObject[answersGroup.recipe.id] === false
          }
          placeholder={answersGroup.recipe?.placeholder}
          onChange={(text) =>
            onTextChange(
              answersGroup.recipe.id,
              text,
              answersGroup.recipe.validation_rule,
              answersGroup.recipe.recipe_question_id
            )
          }
          id={answerDomId}
          value={answersSelectedMap[answersGroup.recipe.id] || ""}
          className={isVariantB ? "textInput-full-width" : ""}
        />
    );

    case "date":
      return (
        <DatePickerWrapper
          disabled={disabled}
          errorMode={questionnaireValidationObject[answersGroup.recipe.id] === false}
          onChange={(text) =>{
            onTextChange(
              answersGroup.recipe.id,
              text,
              answersGroup.recipe.validation_rule,
              answersGroup.recipe.recipe_question_id
              )
            }
          }
          id={answerDomId}
          value={answersSelectedMap[answersGroup.recipe.id]}
          {...(isVariantB
            ? // Variant B DOB: DatePickerWrapper's birthday mode caps the calendar
              // at today − 18y (min age) and opens it there. A non-DOB top-level
              // date stays past-only via questionnaireMaxDate.
              {
                isBirthdayInput: isBirthdayQuestion,
                localizeWeekdays: true,
                maxDate: isBirthdayQuestion ? undefined : questionnaireMaxDate(),
                ...(isWl3 && isBirthdayQuestion
                  ? {
                      placeholderText: translator.translate(language, "default", "QuestionnairePage/date_of_birth_mask"),
                      theme: "care-plus",
                    }
                  : {}),
              }
            : {})}
        />
    );

    case "textarea":
      return (
        <div
          // Variant B: no inset, so the box lines up with the QuestionFrame title.
          style={isVariantB ? { width: "100%", padding: 0 } : { width: "100%" }}
          className="questionnaire-textarea-question"
        >
          <TextArea
            disabled={disabled}
            errorMode={
              questionnaireValidationObject[answersGroup.recipe.id] === false
            }
            onTextChange={(answerID, text) => {
              onTextChange(
                answerID,
                text,
                null,
                answersGroup.recipe.recipe_question_id
              );
            }}
            // Variant B: QuestionFrame already shows label as the page title and
            // help_block as the subtitle, so the placeholder goes inside the box.
            helpblock={isVariantB ? undefined : answersGroup.recipe.help_block}
            placeholder={
              isVariantB
                ? undefined
                : answersGroup.recipe.label || answersGroup.recipe.long_label
            }
            inputPlaceholder={isVariantB ? answersGroup.recipe.placeholder : undefined}
            answerID={answersGroup.recipe.id}
            id={answerDomId}
            value={answersSelectedMap[answersGroup.recipe.id] ? answersSelectedMap[answersGroup.recipe.id] : ""}
          />
        </div>
      );

    default:
      return null;
  }
};

//takes an answer group and renders its content, example:
// answerGroup of type "radio_buttons", will be the anscestor of child (or more) of type radio button
//but this time, those groups act as subquestions
export const RenderAnswerGroupAsSubquestion = ({
  answersGroup,
  onTextChange,
  onAnswerClick,
  onHide,
  onReveal,
  questionnaireValidationObject,
  answersSelectedMap,
  disabled = false,
  files = [],
  setFiles = () => {},
  language,
  category,
  showUploadError = false,
}) => {
  // VariantB renders both shells at once; outside it there is no provider and
  // the id is returned unchanged, so the classic questionnaire keeps bare ids.
  const answerDomId = useShellId(answersGroup?.recipe?.id ? "answer_" + answersGroup.recipe.id : undefined);

  useEffect(() => {
    if (!answersGroup) return;
    onReveal(answersGroup);

    return () => {
      onHide(answersGroup);
    };
  }, []);

    //For subquestions with children:
  if (answersGroup && answersGroup.children?.length > 0) {
    switch (answersGroup.recipe.type) {
      case "radio_buttons":
        return (
          <div className="questionnaire-subquestion">
            <SubquestionText recipeAnswer={answersGroup.recipe} />
            <RadioButtons
              errorMode={
                questionnaireValidationObject[answersGroup.recipe.id] === false
              }
              onAnswerClick={onAnswerClick}
              children={orderAnswerOptionsChildren(
                answersGroup.children,
                answersGroup.recipe.recipe_question_id
              )}
              answersSelectedMap={answersSelectedMap}
            />
          </div>
        );

      case "select": {
        const orderedSelectChildren = orderAnswerOptionsChildren(
          answersGroup.children,
          answersGroup.recipe.recipe_question_id
        );
        const selectedChildren = orderedSelectChildren.filter(option => {
          return answersSelectedMap[option.recipe.id];
        });
        const subquestionInitialValue = selectedChildren.length ? selectedChildren[0].recipe : null;

        return (
          <div className="questionnaire-subquestion">
            <SubquestionText recipeAnswer={answersGroup.recipe} />
            <Select
              disabled={disabled}
              errorMode={
                questionnaireValidationObject[answersGroup.recipe.id] === false
              }
              onAnswerClick={onAnswerClick}
              page={"questionnaire"}
              options={orderedSelectChildren}
              id={answerDomId}
              initialValue={subquestionInitialValue}
            />
          </div>
        );
      }



      case "check_list":
        return (
          <>
            <div className="questionnaire-question-item-text-container  ">
              {answersGroup.recipe.label || answersGroup.recipe.long_label}
            </div>
            {answersGroup.recipe.help_block && (
              <div
                className="questionnaire-question-item-helpblock-container"
                dangerouslySetInnerHTML={{ __html: answersGroup.recipe.help_block }}
              />
            )}
            <CheckBoxList
              errorMode={
                questionnaireValidationObject[answersGroup.recipe.id] === false
              }
              onAnswerClick={onAnswerClick}
              children={orderAnswerOptionsChildren(
                answersGroup.children,
                answersGroup.recipe.recipe_question_id
              )}
              answersSelectedMap={answersSelectedMap}
            />
          </>
        );
    }



    
    //For subquestions without children:
  } else if (
    answersGroup &&
    ["textarea", "date", "number", "text", "photo_diagnose", "document_upload"].includes(
      answersGroup.recipe?.type
    )
  ) {

    switch (answersGroup.recipe.type){
      case "photo_diagnose":
        return (
          <div className="questionnaire-subquestion">
            <SubquestionText recipeAnswer={answersGroup.recipe} />
            <PhotoDiagnose
              language={language}
              category={category}
              files={files}
              setFilesCallback={setFiles}
              showError={showUploadError}
            />
          </div>
        );

      case "document_upload":
        return (
          <div className="questionnaire-subquestion">
            <SubquestionText recipeAnswer={answersGroup.recipe} />
            <DocumentUpload
              language={language}
              files={files}
              setFilesCallback={setFiles}
              showError={showUploadError}
            />
          </div>
        );

      case "date":
          return (
            <div className="questionnaire-subquestion">
              <SubquestionText recipeAnswer={answersGroup.recipe} />
              <div>
                <DatePickerWrapper
                  disabled={disabled}
                  isBirthdayInput={false}
                  maxDate={questionnaireMaxDate()}
                  errorMode={questionnaireValidationObject[answersGroup.recipe.id] === false}
                  onChange={(text) =>{
                    onTextChange(
                      answersGroup.recipe.id,
                      text,
                      answersGroup.recipe.validation_rule,
                      answersGroup.recipe.recipe_question_id
                      )
                    }
                  }
                  id={answerDomId}
                  value={answersSelectedMap[answersGroup.recipe.id]} 
                  
                  />
              </div>
            </div>
          );

      case "number":
        return (
          <div className="questionnaire-subquestion">
            <SubquestionText recipeAnswer={answersGroup.recipe} />
            <NumberInput
              disabled={disabled}
              maxValue={answersGroup.recipe.validation_rule.max_value}
              minValue={answersGroup.recipe.validation_rule.min_value}
              errorMode={questionnaireValidationObject[answersGroup.recipe.id] === false}
              placeholder={answersGroup.recipe.placeholder}
              onChange={(text) => {
                onTextChange(
                  answersGroup.recipe.id,
                  text,
                  answersGroup.recipe.validation_rule,
                  answersGroup.recipe.recipe_question_id
                );
              }}
              id={answerDomId}
              value={answersSelectedMap[answersGroup.recipe.id]}
            />
          </div>
        );

      case "text":
      case "textarea" :
        return (
          <TextArea
            disabled={disabled}
            errorMode={
              questionnaireValidationObject[answersGroup.recipe.id] === false
            }
            helpblock={answersGroup.recipe.help_block}
            placeholder={
              answersGroup.recipe.label || answersGroup.recipe.long_label || answersGroup.recipe.placeholder
            }
            onTextChange={(answerID, text) => {
              onTextChange(
                answerID,
                text,
                null,
                answersGroup.recipe.recipe_question_id
              );
            }}
            answerID={answersGroup.recipe.id}
            id={answerDomId}
            value={answersSelectedMap[answersGroup.recipe.id] ? answersSelectedMap[answersGroup.recipe.id] : "" }
          />
        );
      }
    } 

  return null;
};

//recursive function to get any node from the root question
export const getNode = (root, targetID) => {
  let targetNode;

  if (root?.recipe.id === targetID) {
    //if we found the node, return it
    return root;
  }

  root?.children.forEach((child) => {
    //keep checking starting from every children, and save the result once found
    targetNode = targetNode || getNode(child, targetID);
  });

  return targetNode;
};

export const isQuestionaireBlocking = (questionnaire, answersSelectedMap) => {
  let isBlocking = false;

  questionnaire.forEach((question) => {
    let answers = question.answerGroup.children;

    answers?.forEach((answer) => {
      // the answers? incase we talking about questions type that don't have answers like title or photo diagnos
      let significances = answer.recipe.significances;

      significances.forEach((significance) => {
        if (
          significance.evaluation_type === "contraindications" &&
          answersSelectedMap[answer.recipe.id]
        ) {
          isBlocking = true;
        }
      });
    });
  });

  return isBlocking;
};
